import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { MemoDetailWire } from "@releve/shared";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DangerZone } from "@/components/ui/danger-zone";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SearchableSelect } from "@/components/SearchableSelect";
import { PersonSelect } from "@/components/people/PersonSelect";
import { MemoRecorder } from "./MemoRecorder";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useCreateMemo, useDeleteMemo, useUpdateMemo } from "@/hooks/useMemos";
import { useProjectIndex } from "@/hooks/useProjectIndex";
import { useBusinessIndex } from "@/hooks/useBusinesses";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * What edit mode needs from a memo. Both the index row (`MemoIndexRowWire`)
 * and the detail wire satisfy it, so the grid and the detail page open the
 * same dialog.
 */
export type DialogMemo = Pick<
  MemoDetailWire,
  "id" | "code" | "title" | "description" | "project_id" | "business_id" | "person_id"
>;

interface CreateMemoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit mode when set: no recorder band, a DangerZone at the bottom. */
  memo?: DialogMemo;
}

/**
 * Create (record + details) or edit (details only) a memo — a
 * `FullscreenFormDialog` in the document metaphor (docs/FULLSCREEN_FORM_MODALS.md).
 *
 * Mount it conditionally: its pickers load the project, business and people
 * indexes. Create continues to the new memo's detail page; edit closes in place;
 * the edit-mode DangerZone deletes and returns to the memo list.
 */
export function CreateMemoDialog({ open, onOpenChange, memo }: CreateMemoDialogProps) {
  const { t } = useTranslation("memos");
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const orgNavigate = useOrgNavigate();
  const isEdit = !!memo;

  const createMemo = useCreateMemo();
  const updateMemo = useUpdateMemo();
  const deleteMemo = useDeleteMemo();

  const { data: projects = [] } = useProjectIndex(orgId);
  const { data: businesses = [] } = useBusinessIndex();

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioContentType, setAudioContentType] = useState("audio/webm");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const formSchema = useMemo(
    () =>
      z.object({
        title: z.string().trim().min(1, t("form.titleRequired")).max(200),
        description: z.string().optional(),
        project_id: z.string().optional(),
        business_id: z.string().optional(),
        person_id: z.string().optional(),
        enable_entity_detection: z.boolean().default(false),
      }),
    [t],
  );
  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: memo?.title ?? "",
      description: memo?.description ?? "",
      project_id: memo?.project_id ?? "",
      business_id: memo?.business_id ?? "",
      person_id: memo?.person_id ?? "",
      enable_entity_detection: false,
    },
  });

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` })),
    [projects],
  );
  const businessOptions = useMemo(
    () => businesses.map((b) => ({ value: b.id, label: b.name })),
    [businesses],
  );

  const handleRecordingComplete = (blob: Blob, durationSeconds: number, contentType: string) => {
    setAudioBlob(blob);
    setAudioDuration(durationSeconds);
    setAudioContentType(contentType);
  };

  const handleSubmit = async (values: FormValues) => {
    const details = {
      title: values.title.trim(),
      description: values.description?.trim() || null,
      project_id: values.project_id || null,
      business_id: values.business_id || null,
      person_id: values.person_id || null,
    };

    try {
      if (memo) {
        await updateMemo.mutateAsync({ id: memo.id, ...details });
        onOpenChange(false);
        return;
      }
      if (!audioBlob) return;
      const created = await createMemo.mutateAsync({
        ...details,
        audioBlob,
        durationSeconds: audioDuration,
        contentType: audioContentType,
        enableEntityDetection: values.enable_entity_detection,
      });
      // Continue to the new memo. `replace` BEFORE closing supersedes the
      // modal's history entry (docs/FULLSCREEN_FORM_MODALS.md).
      orgNavigate(`/memos/${created.id}`, { replace: true });
      onOpenChange(false);
    } catch {
      // The mutation hook already toasted; keep the form open.
    }
  };

  const handleDelete = async () => {
    if (!memo) return;
    await deleteMemo.mutateAsync({ id: memo.id });
    orgNavigate("/memos", { replace: true });
    onOpenChange(false);
  };

  const isSubmitting = createMemo.isPending || updateMemo.isPending;

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={memo ? t("dialog.editTitle", { code: memo.code }) : t("dialog.createTitle")}
      description={memo ? t("dialog.editDescription") : t("dialog.createDescription")}
      submitLabel={memo ? t("dialog.saveChanges") : t("dialog.save")}
      isSubmitting={isSubmitting}
      submitDisabled={!isEdit && !audioBlob}
      skipCloseConfirmation={!form.formState.isDirty && !audioBlob}
      onSubmit={form.handleSubmit(handleSubmit)}
      formId="create-memo-form"
    >
      <Form {...form}>
        <div className="space-y-8">
          {/* Links row — outside the card (mirrors the invoice's Project/Fund/AR row) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.project")}</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={projectOptions}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={t("form.selectProject")}
                      searchPlaceholder={t("form.searchProjects")}
                      emptyText={t("form.noProjects")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="business_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.business")}</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={businessOptions}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={t("form.selectBusiness")}
                      searchPlaceholder={t("form.searchBusinesses")}
                      emptyText={t("form.noBusinesses")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="person_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("form.person")}</FormLabel>
                  <FormControl>
                    {/* `quick`: this host is a FullscreenFormDialog, which must
                        not open a second fullscreen dialog. */}
                    <PersonSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder={t("form.selectPerson")}
                      createMode="quick"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Memo Content Card — wraps the subject and (create only) the recording */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              {/* SECTION: subject (hero title + fields) */}
              <div>
                <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                  {t("form.heroTitle")}
                </h2>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("form.title")}
                          </FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">
                              {fieldState.error.message}
                            </span>
                          )}
                        </div>
                        <FormControl>
                          <Input
                            placeholder={t("form.titlePlaceholder")}
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("form.description")}
                          </FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">
                              {fieldState.error.message}
                            </span>
                          )}
                        </div>
                        <FormControl>
                          <Textarea
                            placeholder={t("form.descriptionPlaceholder")}
                            rows={2}
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Full-bleed colored section: the recording (centerpiece) + options.
                  Create only — a memo's audio is fixed once saved.
                  Uses theme-aware `bg-muted` (not the invoice's frozen `bg-releve-gray-light`)
                  because MemoRecorder renders theme-aware text that must stay legible in dark mode. */}
              {!isEdit && (
                <div className="-mx-6 px-6 py-6 bg-muted space-y-5">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      {t("form.recording")}
                    </h3>
                    <MemoRecorder
                      key={open ? "open" : "closed"}
                      onRecordingComplete={handleRecordingComplete}
                      disabled={createMemo.isPending}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="enable_entity_detection"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="mt-0! text-sm font-normal">
                          {t("form.enableEntityDetection")}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Danger zone — edit mode only (shared ui primitive). */}
          {isEdit && (
            <DangerZone
              title={t("dangerZone.title")}
              actionTitle={t("dangerZone.deleteTitle")}
              description={t("dangerZone.deleteDescription")}
              buttonLabel={t("dangerZone.deleteButton")}
              onAction={() => setDeleteOpen(true)}
              disabled={deleteMemo.isPending}
            />
          )}
        </div>
      </Form>

      {isEdit && deleteOpen && (
        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onConfirm={handleDelete}
          title={t("dangerZone.confirmTitle")}
          description={t("dangerZone.confirmDescription")}
          confirmText={t("dangerZone.confirmButton")}
          cancelText={t("dangerZone.cancel")}
          variant="destructive"
        />
      )}
    </FullscreenFormDialog>
  );
}
